import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { config } from "../../../../config";
import {
  calculateDelay,
  RETRYABLE_STATUS_CODES,
  RetryConfig,
} from "./axios.config";
import { asyncStorageService } from "../async-store";

// Determine if an error is retryable
export const shouldRetry = (error: AxiosError): boolean => {
  if (error.response) {
    return RETRYABLE_STATUS_CODES.has(error.response.status);
  }

  // Retry on network errors (ECONNRESET, ETIMEDOUT, etc.)
  return (
    error.code !== undefined &&
    ["ECONNRESET", "ETIMEDOUT", "ECONNABORTED", "ENETUNREACH"].includes(
      error.code
    )
  );
};

// Create the CDN client
const cdnClient = axios.create({
  baseURL: config.cdn.baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

const getCdnClient = async (
  fileUrl: string,
  options: {
    useCache: boolean;
  }
) => {
  console.log("options.useCache: ", options.useCache);
  if (options.useCache) {
    const cachedData = await asyncStorageService.getCdnCache(fileUrl);
    if (cachedData) {
      return cachedData;
    }
  }
  const resp = await cdnClient.get(fileUrl);
  if (!resp || !resp.data) {
    console.log(`Failed to fetch restaurant data for fileUrl: ${fileUrl}`);
    throw new Error(`Failed to fetch restaurant data for fileUrl: ${fileUrl}`);
  }
  console.log("resp.data: ", resp);
  return resp.data;
};

// Add response interceptor for retries
cdnClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const reqConfig = error.config as RetryConfig;

    if (!reqConfig) {
      return Promise.reject(error);
    }

    if (typeof reqConfig.retryCount === "undefined") {
      reqConfig.retryCount = 0;
    }

    // Log the failure with status code if available
    const statusCode = error.response?.status;
    console.log(`CDN call failed. Attempt ${reqConfig.retryCount + 1}`, {
      url: reqConfig.url,
      error: error.message,
      statusCode,
    });

    // Check if we should retry this request
    if (
      shouldRetry(error) &&
      reqConfig.retryCount < config.lambda.retry.maxRetries
    ) {
      reqConfig.retryCount += 1;
      const delay = calculateDelay(reqConfig.retryCount);

      console.log(`Retrying after ${delay}ms... Status code: ${statusCode}`);
      await new Promise((resolve) => setTimeout(resolve, delay));

      return cdnClient(reqConfig);
    }

    // If we shouldn't retry or we've exhausted retries, reject with the error
    if (!shouldRetry(error)) {
      console.log(
        `Not retrying request - non-retryable status code: ${statusCode}`
      );
    } else if (reqConfig.retryCount >= config.lambda.retry.maxRetries) {
      console.log(`Max retries (${config.lambda.retry.maxRetries}) exceeded`);
    }

    return Promise.reject(error);
  }
);

// Export the CDN client
export { getCdnClient };
